/**
 * useBlogPost — fetch + cache the blog companion for a video project.
 *
 * Polls when the user is on the Blog tab so chat-driven edits show up
 * shortly after the orchestrator persists them.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { blogAPI, GetBlogResponse } from '@/services/blogAPI';

interface UseBlogPostOptions {
  /** Whether to poll for updates. Set true when the Blog tab is active. */
  poll?: boolean;
  /** Polling interval in ms. Defaults to 4000. */
  pollIntervalMs?: number;
}

export function useBlogPost(conversationId: string, options: UseBlogPostOptions = {}) {
  const { poll = false, pollIntervalMs = 4000 } = options;

  const [data, setData] = useState<GetBlogResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastVersionRef = useRef<number | null>(null);

  const fetchBlog = useCallback(async () => {
    if (!conversationId) return;
    try {
      const result = await blogAPI.get(conversationId);
      setData(result);
      lastVersionRef.current = result.meta?.version ?? null;
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load blog');
    }
  }, [conversationId]);

  // Initial load
  useEffect(() => {
    if (!conversationId) return;
    setLoading(true);
    fetchBlog().finally(() => setLoading(false));
  }, [conversationId, fetchBlog]);

  // Polling
  useEffect(() => {
    if (!poll || !conversationId) return;
    const id = setInterval(fetchBlog, pollIntervalMs);
    return () => clearInterval(id);
  }, [poll, pollIntervalMs, conversationId, fetchBlog]);

  return {
    data,
    loading,
    error,
    refetch: fetchBlog,
  };
}
